import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject,OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { AnnouncementModalActionsEnum } from '../../../definitions/announcement-modal.enum';
import { AnnouncementTypeEnum } from '../../../definitions/announcement-type.enum';
import { Announcement, AnnouncementBody } from '../../../models/announcement';
import { Customer } from '../../../models/customer';
import { AnnouncementService } from '../../../services/announcement.service';
import { ApiZones } from '../../../services/api.service';
import { CustomSnackbarService } from '../../../services/custom-snackbar.service';
import { CustomerService } from '../../../services/customer.service';
import { LoadingStore } from '../../../stores/loading.store';
import { getStorageObject } from '../../../utils/storage-manager';
import { allSecurityDomains } from '../../main/main.component';

@Component({
  selector: 'app-create-modify-announcement-modal',
  templateUrl: './create-modify-announcement-modal.component.html',
  styleUrls: ['./create-modify-announcement-modal.component.scss']
})
export class CreateModifyAnnouncementModalComponent implements OnInit {
  private builder = inject(FormBuilder);
  private dialogRef = inject(MatDialogRef<CreateModifyAnnouncementModalComponent>);
  private loading = inject(LoadingStore);
  private translateService = inject(TranslateService);
  private customerService = inject(CustomerService);
  private announcementService = inject(AnnouncementService);
  private snackbarService = inject(CustomSnackbarService);

  protected data: { [key: string]: AnnouncementModalActionsEnum } = inject(MAT_DIALOG_DATA);

  /**
   * Announcement form
   */
  announcementForm: FormGroup;

  /**
   * Verticals options
   */
  verticals: Customer[] = [];

  /**
   * Announcement types
   */
  announcementTypes: AnnouncementTypeEnum[] = Object.values(AnnouncementTypeEnum);

  /**
   * Current active announcement
   */
  currentActiveAnnouncement?: Announcement;

  /**
   * Whether we are creating or modifying an announcement
   */
  isModifying = this.data.action === AnnouncementModalActionsEnum.MODIFY;

  constructor() {
    this.announcementForm = this.builder.group({
      verticals: ['', Validators.required],
      type: [AnnouncementTypeEnum.GENERIC],
      body: this.builder.group({
        es: ['', Validators.required],
        en: ['', Validators.required],
        it: ['', Validators.required]
      }),
      startDateCheckBox: [false],
      endDateCheckBox: [false]
    });
  }

  async ngOnInit(): Promise<void> {
    if (this.isModifying) {
      try {
        this.currentActiveAnnouncement = await this.announcementService.getCurrentActiveAnnouncement().toPromise();
        if (this.currentActiveAnnouncement) {
          this.announcementForm.patchValue({
            verticals: this.currentActiveAnnouncement.customers,
            type: this.currentActiveAnnouncement.type,
            body: {
              es: this.currentActiveAnnouncement.body.es,
              en: this.currentActiveAnnouncement.body.en,
              it: this.currentActiveAnnouncement.body.it
            },
            startDateCheckBox: this.currentActiveAnnouncement.startDate !== null,
            endDateCheckBox: this.currentActiveAnnouncement.endDate !== null
          });

          this.addRemoveStartDate(this.currentActiveAnnouncement.startDate);
          this.addRemoveEndDate(this.currentActiveAnnouncement.endDate);
        }
      } catch (err) {
        if (err instanceof HttpErrorResponse) {
          this.snackbarService.showTranslatedError(err);
        }

        this.closeModal();
      }
    }

    try {
      let region: string | string[] = getStorageObject('region');
      if(region instanceof Array){
        region = region[0];
      }
      const customerListResponse = await this.customerService.listCustomersByZone(
        ApiZones[region as keyof typeof ApiZones]
      ).toPromise();
      this.verticals = customerListResponse.items.filter((customer) => !allSecurityDomains.includes(customer.domain));
    } catch (err) {
      if (err instanceof HttpErrorResponse) {
        this.snackbarService.showTranslatedError(err);
      }
    }
  }

  /**
   * Create or modify current active announcement.
   * * If you modify it, and you do not select at least the same verticals as before it shows an error snackbar
   */
  async createModifyAnnouncement(): Promise<void> {
    if (!this.isFormValid()) {
      return;
    }

    try {
      this.loading.start();

      const newAnnouncement = new Announcement(
        this.announcementForm.value.verticals,
        this.announcementForm.value.type,
        {
          es: this.announcementForm.value.body.es || '',
          en: this.announcementForm.value.body.en || '',
          it: this.announcementForm.value.body.it || ''
        },
        this.announcementForm.value.startDate ? this.announcementForm.value.startDate.toISOString() : null,
        this.announcementForm.value.endDate ? this.announcementForm.value.endDate.toISOString() : null
      );

      if (this.isModifying) {
        await this.announcementService.updateCurrentActiveAnnouncement(newAnnouncement).toPromise();
      } else {
        await this.announcementService.createAnnouncement(newAnnouncement).toPromise();
      }
      this.closeModal(true);
    } catch (err) {
      if (err instanceof HttpErrorResponse) {
        this.snackbarService.showTranslatedError(err);
      }
    } finally {
      this.loading.stop();
    }
  }

  /**
   * Check if the form is valid through the following checks:
   *
   * 1. If the form is invalid
   * 2. If we are modifying an announcement, check if at least the same verticals are selected
   * 3. If the startDate is after the endDate
   *
   */
  isFormValid(): boolean {
    if (this.announcementForm.invalid) {
      return false;
    }

    if (this.isModifying) {
      const nonDeletedVerticals = this.currentActiveAnnouncement?.customers.every((customer) =>
        this.announcementForm.value.verticals.includes(customer)
      );

      if (!nonDeletedVerticals) {
        this.snackbarService.present(this.translateService.instant('announcements.modifyModal.deleteCustomers'));

        return false;
      }
    }

    if (
      this.announcementForm.value.startDate &&
      this.announcementForm.value.endDate &&
      this.announcementForm.value.startDate > this.announcementForm.value.endDate
    ) {
      this.snackbarService.present(this.translateService.instant('announcements.dateIntervalError'));

      return false;
    }

    return true;
  }

  /**
   * Add/remove startDate and its validators from the formGroup
   * @param defaultValue Date to show as default one
   */
  addRemoveStartDate(defaultValue?: Date): void {
    if (this.announcementForm.controls.startDateCheckBox.value) {
      this.announcementForm.addControl(
        'startDate',
        this.builder.control(defaultValue ? defaultValue : new Date(), Validators.required)
      );
    } else {
      this.announcementForm.removeControl('startDate');
    }
  }

  /**
   * Add/remove endDate and its validators from the formGroup
   * @param defaultValue Date to show as default one
   */
  addRemoveEndDate(defaultValue?: Date): void {
    if (this.announcementForm.controls.endDateCheckBox.value) {
      this.announcementForm.addControl(
        'endDate',
        this.builder.control(defaultValue ? defaultValue : '', Validators.required)
      );
    } else {
      this.announcementForm.removeControl('endDate');
    }
  }

  /**
   * Method to close the modal
   * @param success Whether the action was successful
   */
  closeModal(success = false): void {
    this.dialogRef.close(success);
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  protected readonly AnnouncementBody = AnnouncementBody;
}
